/**
 * Utilitaires pour nettoyer les payloads avant envoi à l'API
 *
 * Résout le problème: BD retourne `null` → Frontend garde `null` →
 * Backend Zod attend `undefined` → ERREUR
 *
 * Solution: Nettoyer les données côté Frontend avant envoi
 */

/**
 * Nettoie un objet en supprimant les valeurs null et les chaînes vides
 * Convertit null → undefined (non envoyé en JSON)
 * Convertit "" → undefined (non envoyé en JSON)
 *
 * @param payload - L'objet à nettoyer
 * @param options - Options de nettoyage
 * @returns L'objet nettoyé (sans les clés undefined)
 */
export function cleanPayload<T extends Record<string, unknown>>(
  payload: T,
  options: {
    /** Supprimer les chaînes vides (défaut: true) */
    removeEmptyStrings?: boolean;
    /** Supprimer les valeurs null (défaut: true) */
    removeNulls?: boolean;
    /** Champs à exclure du nettoyage (garder tels quels) */
    excludeFields?: string[];
    /** Nettoyer récursivement les objets imbriqués (défaut: false) */
    deep?: boolean;
  } = {}
): Partial<T> {
  const {
    removeEmptyStrings = true,
    removeNulls = true,
    excludeFields = [],
    deep = false,
  } = options;

  const result: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(payload)) {
    // Exclure certains champs du nettoyage
    if (excludeFields.includes(key)) {
      result[key] = value;
      continue;
    }

    // Supprimer les null
    if (value === null && removeNulls) {
      continue;
    }

    // Supprimer les chaînes vides
    if (typeof value === 'string' && value.trim() === '' && removeEmptyStrings) {
      continue;
    }

    // Nettoyage récursif pour les objets imbriqués
    if (deep && typeof value === 'object' && value !== null && !Array.isArray(value)) {
      result[key] = cleanPayload(value as Record<string, unknown>, options);
      continue;
    }

    // Garder la valeur
    result[key] = value;
  }

  return result as Partial<T>;
}

/**
 * Nettoie un payload pour Company (champs spécifiques)
 *
 * @param company - L'objet company à nettoyer
 * @returns L'objet company nettoyé
 */
export function cleanCompanyPayload<T extends Record<string, unknown>>(company: T): Partial<T> {
  // Champs qui ne doivent jamais être supprimés même si vides
  const excludeFields = ['id', 'tenantId', 'createdAt', 'updatedAt'];

  return cleanPayload(company, {
    removeEmptyStrings: true,
    removeNulls: true,
    excludeFields,
    deep: false,
  });
}

/**
 * Nettoie un payload pour Client
 */
export function cleanClientPayload<T extends Record<string, unknown>>(client: T): Partial<T> {
  const excludeFields = ['id', 'tenantId', 'companyId', 'createdAt', 'updatedAt'];

  return cleanPayload(client, {
    removeEmptyStrings: true,
    removeNulls: true,
    excludeFields,
    deep: false,
  });
}

/**
 * Nettoie un payload pour Product
 */
export function cleanProductPayload<T extends Record<string, unknown>>(product: T): Partial<T> {
  const excludeFields = ['id', 'tenantId', 'companyId', 'createdAt', 'updatedAt'];

  return cleanPayload(product, {
    removeEmptyStrings: true,
    removeNulls: true,
    excludeFields,
    deep: false,
  });
}

/**
 * Nettoie un payload générique pour tout type d'entité
 */
export function cleanEntityPayload<T extends Record<string, unknown>>(
  entity: T,
  entityType: 'company' | 'client' | 'product' | 'invoice' | 'quote' | 'generic' = 'generic'
): Partial<T> {
  const baseExcludeFields = ['id', 'tenantId', 'companyId', 'createdAt', 'updatedAt'];

  const additionalExcludes: Record<string, string[]> = {
    invoice: ['number', 'status', 'totalHT', 'totalTTC'],
    quote: ['number', 'status', 'totalHT', 'totalTTC'],
    company: [],
    client: [],
    product: [],
    generic: [],
  };

  return cleanPayload(entity, {
    removeEmptyStrings: true,
    removeNulls: true,
    excludeFields: [...baseExcludeFields, ...additionalExcludes[entityType]],
    deep: false,
  });
}
